import React, { useState, useCallback, useMemo, memo } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Chip, Grid,
  Divider, Tooltip, IconButton, Collapse, LinearProgress,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import SearchIcon        from '@mui/icons-material/Search';
import ArrowBackIcon     from '@mui/icons-material/ArrowBack';
import FilterAltIcon     from '@mui/icons-material/FilterAlt';
import ScheduleIcon      from '@mui/icons-material/Schedule';
import CategoryIcon      from '@mui/icons-material/Category';
import LocationOnIcon    from '@mui/icons-material/LocationOn';
import HelpOutlineIcon   from '@mui/icons-material/HelpOutline';
import RouteIcon         from '@mui/icons-material/Route';
import WarningAmberIcon  from '@mui/icons-material/WarningAmber';
import SpeedIcon         from '@mui/icons-material/Speed';
import PanToolIcon       from '@mui/icons-material/PanTool';
import FenceIcon         from '@mui/icons-material/Fence';
import OpenInFullIcon    from '@mui/icons-material/OpenInFull';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import ExpandLessIcon    from '@mui/icons-material/ExpandLess';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InboxIcon         from '@mui/icons-material/Inbox';
import AssessmentIcon    from '@mui/icons-material/Assessment';
import AccessTimeIcon    from '@mui/icons-material/AccessTime';
import PictureAsPdfIcon  from '@mui/icons-material/PictureAsPdf';
import MapIcon           from '@mui/icons-material/Map';
import CloseIcon         from '@mui/icons-material/Close';
import RoomIcon          from '@mui/icons-material/Room';

import VehicleAlertMapModal from '../cards/VehicleAlertMapModal';
import { AREA_OPTIONS }     from '../../utils/constants';
import { isAdminUser, getLoggedInArea, getAreaDisplayName } from '../../utils/authService';

// ── Constants ─────────────────────────────────────────────────────────────────

const VTS_API_BASE  = 'https://bbstate.cclai.in';
const VISIBLE_LIMIT = 25;

const DISPLAY_TO_VTS_AREA = {
  'Barka Sayal':             'barkasayal',
  'Argada':                  'argada',
  'North Karanpura':         'northkaranpura',
  'Bokaro & Kargali':        'bokarokargali',
  'Kathara':                 'kathara',
  'Giridih':                 'giridih',
  'Amrapali & Chandragupta': 'amrapali',
  'Magadh & Sanghmitra':     'magadh',
  'Rajhara':                 'rajhara',
  'Kuju':                    'kuju',
  'Hazaribagh':              'hazaribagh',
  'Rajrappa':                'rajrappa',
  'Dhori':                   'dhori',
  'Piparwar':                'piparwar',
};

const VTS_AREA_TO_DISPLAY = Object.fromEntries(
  Object.entries(DISPLAY_TO_VTS_AREA).map(([k, v]) => [v, k])
);

// Maps authService area keys → the VTS report `area` field value used for filtering.
// Needed for compound auth keys whose normalized form doesn't match the VTS area field.
// e.g. 'magadh-sanghmitra' normalises to 'magadhsanghmitra' ≠ 'magadh' in DB.
// Single-hyphen keys like 'barka-sayal' → 'barkasayal' already match via normalizeAreaKey
// so they don't need an explicit entry here, but it doesn't hurt to include them.
const AUTH_TO_VTS_AREA = {
  'amrapali-chandragupta': 'amrapali',
  'amrapali':              'amrapali',
  'magadh-sanghmitra':     'magadh',
  'magadh':                'magadh',
  'barka-sayal':           'barkasayal',
  'barkasayal':            'barkasayal',
  'north-karanpura':       'northkaranpura',
  'northkaranpura':        'northkaranpura',
  'bokaro-kargali':        'bokarokargali',
  'bokarokargali':         'bokarokargali',
};

const EVENT_TYPES = [
  { id: 'OR',  label: 'Off-Route',         apiKey: 'offroute',  alertType: 'off-route',  color: '#4ECDC4', Icon: RouteIcon },
  { id: 'OA',  label: 'Off-Area',          apiKey: 'offarea',   alertType: 'off-area',   color: '#E91E8C', Icon: FenceIcon },
  { id: 'TA',  label: 'Tamper',            apiKey: 'tamper',    alertType: 'tamper',     color: '#DC143C', Icon: WarningAmberIcon },
  { id: 'OS',  label: 'Over-Speed',        apiKey: 'overspeed', alertType: 'overspeed',  color: '#F39C12', Icon: SpeedIcon },
  { id: 'USP', label: 'Unauthorized Stop', apiKey: 'stoppage',  alertType: 'stoppage',   color: '#9B59B6', Icon: PanToolIcon },
];

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  paper: {
    width: '88%', maxWidth: '620px', borderRadius: 3,
    backgroundColor: 'rgba(18,24,32,0.97)', color: 'white',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)', overflow: 'hidden',
  },
  sectionHeader: { display: 'flex', alignItems: 'center', mb: 1.5 },
  sectionIcon:   { mr: 1, color: '#1ABC9C', fontSize: 18 },
  sectionTitle:  { fontWeight: 600, color: 'rgba(255,255,255,0.9)', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: 0.8 },
  helpIcon:      { ml: 0.5, color: 'rgba(255,255,255,0.3)', padding: '2px' },
  dateTimeField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 1.5,
      '&:hover .MuiOutlinedInput-notchedOutline':       { borderColor: '#1ABC9C', borderWidth: '1.5px' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1ABC9C', borderWidth: '2px' },
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
    },
    '& .MuiInputLabel-root':             { color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#1ABC9C' },
    '& .MuiOutlinedInput-input':         { color: 'white', fontSize: '0.85rem' },
    '& svg': { color: 'rgba(255,255,255,0.4)' },
  },
  selectInput: {
    backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', borderRadius: 1.5, fontSize: '0.85rem',
    '& .MuiOutlinedInput-notchedOutline':             { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover .MuiOutlinedInput-notchedOutline':       { borderColor: '#1ABC9C', borderWidth: '1.5px' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1ABC9C', borderWidth: '2px' },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.4)' },
  },
  selectMenu: {
    PaperProps: { style: { maxHeight: 300, backgroundColor: 'rgba(18,28,40,0.99)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 } },
  },
  menuItem: {
    color: 'rgba(255,255,255,0.85)', fontSize: '0.84rem',
    '&:hover':     { backgroundColor: 'rgba(26,188,156,0.12)' },
    '&.Mui-selected': { backgroundColor: 'rgba(26,188,156,0.18)', '&:hover': { backgroundColor: 'rgba(26,188,156,0.25)' } },
  },
  divider: { borderColor: 'rgba(255,255,255,0.06)' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatISO = (date) => {
  if (!date) return '';
  const p = (n) => String(n).padStart(2, '0');
  // DB stores UTC (5:30 behind IST). User picks IST → subtract 5:30 → send UTC.
  // getUTC* on a local-IST Date gives the UTC equivalent automatically.
  return `${date.getUTCFullYear()}-${p(date.getUTCMonth()+1)}-${p(date.getUTCDate())}T${p(date.getUTCHours())}:${p(date.getUTCMinutes())}:${p(date.getUTCSeconds())}`;
};

// The VTS DB stores event times as UTC wall-clock, but mysql2 on the API server
// parses those DATETIME values in the server's OWN timezone (IST) before
// serialising them to JSON. The result is an ISO string tagged 'Z' whose instant
// is 5h30m EARLIER than the time it actually represents:
//
//   DB value       2026-09-23 08:07:57      (already UTC)
//   API returns    2026-09-23T02:37:57.000Z (mis-tagged, 5:30 early)
//   True instant   2026-09-23T08:07:57Z   → 13:37:57 IST
//
// Without adding the skew back, toReadable()'s +5:30 for IST merely cancels
// mysql2's -5:30 and the UI ends up printing the raw UTC value.
//
// NOTE: this compensates for a backend defect. The real fix is to set mysql2's
// `timezone: 'Z'` (or `dateStrings: true`) in the API's pool config — once that
// lands, DELETE SERVER_TZ_SKEW_MS and the shape-2 branch below, or times will
// jump 5:30 ahead.
const SERVER_TZ_SKEW_MS = 5.5 * 60 * 60 * 1000;

// Resolves a raw API timestamp to the true instant of the event, or null.
const toEventDate = (ts) => {
  if (!ts) return null;

  // Shape 1 — plain DB string ("2026-09-23 08:07:57"): the raw UTC wall-clock
  // with no zone marker, so there is no mysql2 skew to undo. Tag it as UTC;
  // the browser would otherwise read it as local time.
  if (typeof ts === 'string' && ts.includes(' ') && !ts.includes('Z') && !ts.includes('+')) {
    const d = new Date(ts.replace(' ', 'T') + 'Z');
    return isNaN(d.getTime()) ? null : d;
  }

  // Shape 2 — an explicit numeric offset ("...+05:30"): unambiguous, and never
  // produced by JSON Date serialisation, so trust it as given.
  if (typeof ts === 'string' && /[+-]\d{2}:?\d{2}$/.test(ts)) {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }

  // Shape 3 — a Date the API serialised: skewed 5:30 early, so add it back.
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getTime() + SERVER_TZ_SKEW_MS);
};

const toReadable = (ts) => {
  const d = toEventDate(ts);
  if (!d) return ts || 'N/A';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

// Normalize area keys to lowercase without hyphens for comparison
// (authService may return 'barka-sayal' while backend returns 'barkasayal')
const normalizeAreaKey = (a) => (a || '').toLowerCase().replace(/-/g, '');

const toShortDate = (date) =>
  date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const transformEvent = (item, alertType) => {
  const lat = parseFloat(item.latitude);
  const lng = parseFloat(item.longitude);
  const result = {
    ...item, alertType,
    vehicleNumber:      item.deviceName || item.uniqueId || 'Unknown',
    vehicleTransporter: item.transporter || '',
    readableTime:       toReadable(item.timestamp),
    currentLocation:    { lat: isNaN(lat) ? 0 : lat, lng: isNaN(lng) ? 0 : lng },
    coordinates:        `${item.latitude}, ${item.longitude}`,
    // For off-area events the "area" is the geofenced area boundary that was crossed,
    // so use offAreaName (set by backend from GEOFENCE_TO_AREA) as the primary display.
    areaDisplay: alertType === 'off-area'
      ? (item.offAreaName || VTS_AREA_TO_DISPLAY[item.area] || item.groupName || item.area || 'Unknown')
      : (VTS_AREA_TO_DISPLAY[item.area] || item.groupName || item.area || 'Unknown'),
    // Canonical area name for grouping/breakdown — always uses VTS_AREA_TO_DISPLAY
    // so off-area offAreaName variants don't create duplicate buckets.
    areaGroup: VTS_AREA_TO_DISPLAY[item.area] || item.groupName || item.area || 'Unknown',
  };
  if (alertType === 'tamper')    result.alertSubType = item.type || '';
  if (alertType === 'overspeed') { result.actualSpeed = item.actualSpeed; result.allowedSpeed = item.allowedSpeed; }
  return result;
};

// ── SectionLabel ──────────────────────────────────────────────────────────────

const SectionLabel = memo(({ icon: IconComp, label, tooltip }) => (
  <Box sx={S.sectionHeader}>
    <IconComp sx={S.sectionIcon} />
    <Typography variant="caption" sx={S.sectionTitle}>{label}</Typography>
    {tooltip && (
      <Tooltip title={tooltip} placement="top">
        <IconButton size="small" sx={S.helpIcon}><HelpOutlineIcon sx={{ fontSize: 14 }} /></IconButton>
      </Tooltip>
    )}
  </Box>
));

// ── KPI Card ──────────────────────────────────────────────────────────────────

const KpiCard = memo(({ typeConfig, count, total }) => {
  const { Icon, color, label } = typeConfig;
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <Box sx={{
      flex: '1 1 0', minWidth: 0,
      bgcolor: 'rgba(0,0,0,0.35)',
      border: `1px solid ${color}30`,
      borderTop: `3px solid ${color}`,
      borderRadius: 2, p: 2,
      position: 'relative', overflow: 'hidden',
      boxShadow: `0 4px 20px ${color}12`,
    }}>
      {/* Faint background glow */}
      <Box sx={{ position: 'absolute', top: -20, right: -20, width: 70, height: 70, borderRadius: '50%', bgcolor: color, opacity: 0.06, filter: 'blur(16px)', pointerEvents: 'none' }} />

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.25 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: `${color}18`, border: `1.5px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 10px ${color}20` }}>
          <Icon sx={{ color, fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color, bgcolor: `${color}18`, border: `1px solid ${color}35`, px: 0.75, py: 0.3, borderRadius: 1 }}>
          {pct}%
        </Typography>
      </Box>

      <Typography variant="h4" fontWeight={900} sx={{ color: 'white', lineHeight: 1, mb: 0.4, fontSize: '1.9rem' }}>{count}</Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.69rem', display: 'block', mb: 1.25, fontWeight: 500 }}>{label}</Typography>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)', '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 2 } }} />
    </Box>
  );
});

// ── EventRow ──────────────────────────────────────────────────────────────────

const SEV_COLOR = { Critical: '#DC143C', High: '#E67E22', Medium: '#F1C40F', Low: '#2ECC71' };

const EventRow = memo(({ event, color, onClick }) => {
  const sev      = event.severity || 'High';
  const sevColor = SEV_COLOR[sev] || '#E67E22';
  return (
    <Box
      onClick={() => onClick(event)}
      sx={{
        px: 2, py: 1.5, cursor: 'pointer',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        display: 'flex', alignItems: 'center', gap: 1.5,
        transition: 'background 0.1s',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
        '&:hover .map-btn': { opacity: 1 },
      }}
    >
      {/* Left accent bar */}
      <Box sx={{ width: 3, minHeight: 48, alignSelf: 'stretch', bgcolor: color, borderRadius: 2, flexShrink: 0 }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Row 1: vehicle + severity */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <DirectionsCarIcon sx={{ color, fontSize: 13, opacity: 0.9 }} />
            <Typography variant="body2" fontWeight={700} sx={{ color: 'white', fontSize: '0.83rem', letterSpacing: 0.2 }}>
              {event.vehicleNumber}
            </Typography>
          </Box>
          <Chip label={sev} size="small" sx={{ bgcolor: `${sevColor}18`, color: sevColor, border: `1px solid ${sevColor}40`, fontSize: '0.6rem', height: 17, fontWeight: 700 }} />
        </Box>

        {/* Row 2: time */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
          <AccessTimeIcon sx={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.71rem' }}>{event.readableTime}</Typography>
        </Box>

        {/* Row 3: area */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocationOnIcon sx={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.7rem' }} noWrap>
            {event.areaDisplay}{event.groupName && !event.geofenceName ? ` • ${event.groupName}` : ''}
          </Typography>
        </Box>

        {/* Row 4: geofence chip — only for alert types that carry one */}
        {event.geofenceId && event.geofenceName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
            <RoomIcon sx={{ fontSize: 11, color: 'rgba(78,205,196,0.55)' }} />
            <Typography variant="caption" sx={{ color: 'rgba(78,205,196,0.75)', fontSize: '0.68rem' }} noWrap>
              {event.geofenceName}
            </Typography>
          </Box>
        )}

        {/* Row 4: transporter */}
        {event.vehicleTransporter && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <LocalShippingIcon sx={{ fontSize: 11, color: 'rgba(255,255,255,0.2)' }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.28)', fontSize: '0.69rem' }} noWrap>{event.vehicleTransporter}</Typography>
          </Box>
        )}

        {/* Speed badge */}
        {event.alertType === 'overspeed' && event.actualSpeed != null && (
          <Box sx={{ mt: 0.5, display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(243,156,18,0.1)', border: '1px solid rgba(243,156,18,0.25)', borderRadius: 1, px: 0.75, py: 0.2 }}>
            <SpeedIcon sx={{ fontSize: 11, color: '#F39C12' }} />
            <Typography variant="caption" sx={{ color: '#F39C12', fontWeight: 700, fontSize: '0.69rem' }}>
              {event.actualSpeed} km/h &nbsp;/&nbsp; limit {event.allowedSpeed} km/h
            </Typography>
          </Box>
        )}

        {/* Tamper subtype */}
        {event.alertType === 'tamper' && event.alertSubType && (
          <Typography variant="caption" sx={{ color: 'rgba(220,20,60,0.75)', fontSize: '0.68rem', display: 'block', mt: 0.3 }}>
            Subtype: {event.alertSubType}
          </Typography>
        )}
      </Box>

      <Tooltip title="View on map">
        <IconButton className="map-btn" size="small"
          sx={{ opacity: 0, flexShrink: 0, color: color, bgcolor: `${color}12`, border: `1px solid ${color}28`, width: 28, height: 28, transition: 'opacity 0.12s', '&:hover': { bgcolor: `${color}25` } }}>
          <OpenInFullIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
});

// ── EventTypeCard ─────────────────────────────────────────────────────────────

const EventTypeCard = memo(({ typeConfig, events, onEventClick, onExpand }) => {
  const [expanded, setExpanded] = useState(true);
  const { Icon, color, label } = typeConfig;
  const visible = events.slice(0, VISIBLE_LIMIT);

  return (
    <Box sx={{
      bgcolor: 'rgba(14,20,28,0.92)',
      border: `1px solid ${color}35`,
      borderTop: `3px solid ${color}`,
      borderRadius: 2.5,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 180,
      boxShadow: `0 4px 28px ${color}14, 0 1px 0 ${color}20 inset`,
    }}>

      {/* ── Card Header ── */}
      <Box
        onClick={() => setExpanded((e) => !e)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 2, cursor: 'pointer',
          background: `linear-gradient(135deg, ${color}20 0%, ${color}08 55%, rgba(0,0,0,0) 100%)`,
          borderBottom: `1px solid ${color}20`,
          transition: 'background 0.15s',
          '&:hover': { background: `linear-gradient(135deg, ${color}2e 0%, ${color}10 55%, rgba(0,0,0,0) 100%)` },
          userSelect: 'none',
        }}
      >
        {/* Left: icon + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 1.5,
            bgcolor: `${color}1e`, border: `1.5px solid ${color}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 0 16px ${color}28`,
          }}>
            <Icon sx={{ color, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography fontWeight={800} sx={{ color: 'white', fontSize: '0.95rem', lineHeight: 1.25, letterSpacing: 0.2 }}>
              {label}
            </Typography>
            <Typography sx={{ color: `${color}cc`, fontSize: '0.69rem', fontWeight: 600, mt: 0.15 }}>
              {events.length} alert{events.length !== 1 ? 's' : ''} found
            </Typography>
          </Box>
        </Box>

        {/* Right: fullscreen button + count circle + chevron */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {events.length > 0 && (
            <Tooltip title="Expand fullscreen" placement="top">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onExpand && onExpand(); }}
                sx={{
                  color: `${color}99`, bgcolor: `${color}12`,
                  border: `1px solid ${color}28`, width: 30, height: 30,
                  '&:hover': { bgcolor: `${color}22`, color, border: `1px solid ${color}55` },
                  transition: 'all 0.15s',
                }}
              >
                <OpenInFullIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{
            width: 40, height: 40, borderRadius: '50%',
            bgcolor: color, color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 900, fontSize: events.length > 99 ? '0.78rem' : '0.95rem',
            boxShadow: `0 3px 12px ${color}55`,
            letterSpacing: -0.5,
          }}>
            {events.length > 999 ? '999+' : events.length}
          </Box>
          {expanded
            ? <ExpandLessIcon sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 20 }} />
            : <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 20 }} />}
        </Box>
      </Box>

      {/* ── Event List ── */}
      <Collapse in={expanded} timeout={180}>
        <Box sx={{
          maxHeight: 440, overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 3 },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bgcolor: `${color}30`, borderRadius: 4 },
        }}>
          {events.length === 0 ? (
            <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: `${color}0c`, border: `1px solid ${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <InboxIcon sx={{ color: `${color}40`, fontSize: 22 }} />
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.78rem' }}>No events in this period</Typography>
            </Box>
          ) : (
            <>
              {visible.map((event, i) => (
                <EventRow key={event.id ?? i} event={event} color={color} onClick={onEventClick} />
              ))}
              {events.length > VISIBLE_LIMIT && (
                <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${color}15`, display: 'flex', justifyContent: 'center' }}>
                  <Button size="small" startIcon={<OpenInFullIcon sx={{ fontSize: 13 }} />}
                    onClick={(e) => { e.stopPropagation(); onExpand && onExpand(); }}
                    sx={{ color, fontSize: '0.73rem', fontWeight: 700, bgcolor: `${color}0a`,
                          border: `1px solid ${color}22`, borderRadius: '6px', px: 2,
                          '&:hover': { bgcolor: `${color}18` } }}>
                    View all {events.length} alerts in fullscreen
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
});

// ── Area Breakdown Row ────────────────────────────────────────────────────────

const AreaBreakdownRow = memo(({ areaBreakdown, total }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? areaBreakdown : areaBreakdown.slice(0, 6);

  if (areaBreakdown.length === 0) return null;

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.05)', bgcolor: 'rgba(0,0,0,0.12)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <MapIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)' }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 600 }}>
            Area Breakdown
          </Typography>
        </Box>
        {areaBreakdown.length > 6 && (
          <Button size="small" onClick={() => setShowAll((s) => !s)}
            sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.67rem', py: 0, minWidth: 0, '&:hover': { color: '#1ABC9C' } }}>
            {showAll ? 'Less' : `+${areaBreakdown.length - 6} more`}
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {visible.map(([area, count]) => {
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <Box key={area} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 1.5, px: 1, py: 0.4 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.71rem', fontWeight: 500 }}>{area}</Typography>
              <Box sx={{ width: 1, height: 10, bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 1 }} />
              <Typography variant="caption" sx={{ color: '#1ABC9C', fontSize: '0.71rem', fontWeight: 700 }}>{count}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem' }}>({pct}%)</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});

// ── Fullscreen Type Modal ─────────────────────────────────────────────────────

const FullscreenTypeModal = memo(({ typeConfig, events, onClose, onEventClick }) => {
  const { Icon, color, label } = typeConfig;
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return events;
    const q = search.toLowerCase();
    return events.filter((e) =>
      (e.vehicleNumber       || '').toLowerCase().includes(q) ||
      (e.areaDisplay         || '').toLowerCase().includes(q) ||
      (e.vehicleTransporter  || '').toLowerCase().includes(q)
    );
  }, [events, search]);

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 1400,
      bgcolor: 'rgba(6,10,16,0.95)', backdropFilter: 'blur(14px)',
      display: 'flex', flexDirection: 'column',
      animation: 'vtsModalIn 0.18s cubic-bezier(.4,0,.2,1)',
      '@keyframes vtsModalIn': {
        from: { opacity: 0, transform: 'scale(0.97)' },
        to:   { opacity: 1, transform: 'scale(1)' },
      },
    }}>

      {/* ── Header ── */}
      <Box sx={{
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 1.5,
        px: { xs: 2, sm: 3 }, py: 2,
        borderBottom: `2px solid ${color}`,
        background: `linear-gradient(135deg, ${color}1a 0%, ${color}08 60%, rgba(0,0,0,0) 100%)`,
      }}>
        {/* Left: icon + title + count */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2,
            bgcolor: `${color}20`, border: `2px solid ${color}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 0 20px ${color}30`,
          }}>
            <Icon sx={{ color, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography fontWeight={900} sx={{ color: 'white', fontSize: '1.1rem', lineHeight: 1.2 }}>{label}</Typography>
            <Typography sx={{ color: `${color}bb`, fontSize: '0.72rem', fontWeight: 600, mt: 0.2 }}>
              {filtered.length !== events.length
                ? `${filtered.length} of ${events.length} alerts`
                : `${events.length} alert${events.length !== 1 ? 's' : ''} total`}
            </Typography>
          </Box>
          <Chip
            label={events.length > 999 ? '999+' : events.length}
            sx={{
              bgcolor: color, color: 'white', fontWeight: 900,
              fontSize: '0.88rem', height: 32, minWidth: 44,
              borderRadius: '16px', boxShadow: `0 3px 14px ${color}55`,
            }}
          />
        </Box>

        {/* Right: search + close */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Search box */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px', px: 1.5, py: 0.75,
            minWidth: { xs: 140, sm: 230 },
            '&:focus-within': { borderColor: color, boxShadow: `0 0 0 2px ${color}22` },
            transition: 'all 0.15s',
          }}>
            <SearchIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
            <Box
              component="input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vehicle, area…"
              sx={{
                border: 'none', background: 'transparent', outline: 'none',
                color: 'white', fontSize: '0.82rem', width: '100%',
                '&::placeholder': { color: 'rgba(255,255,255,0.3)' },
              }}
            />
            {search && (
              <Box component="span" onClick={() => setSearch('')}
                sx={{ cursor: 'pointer', color: 'rgba(255,255,255,0.35)', lineHeight: 1, fontSize: '1.1rem', '&:hover': { color: 'white' } }}>
                ×
              </Box>
            )}
          </Box>

          <Tooltip title="Close fullscreen (Esc)">
            <IconButton onClick={onClose}
              sx={{
                color: 'rgba(255,255,255,0.55)', bgcolor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', color: 'white' },
                transition: 'all 0.15s',
              }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Scrollable event list ── */}
      <Box sx={{
        flex: 1, overflowY: 'auto',
        px: { xs: 1, sm: 2, md: 3 }, py: 2,
        '&::-webkit-scrollbar': { width: 5 },
        '&::-webkit-scrollbar-track': { bgcolor: 'rgba(255,255,255,0.02)' },
        '&::-webkit-scrollbar-thumb': { bgcolor: `${color}35`, borderRadius: 4 },
      }}>
        {filtered.length === 0 ? (
          <Box sx={{ py: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <InboxIcon sx={{ color: `${color}30`, fontSize: 44 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.9rem' }}>
              {search ? `No results for "${search}"` : 'No events in this period'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            maxWidth: 920, mx: 'auto',
            bgcolor: 'rgba(14,20,28,0.85)', borderRadius: 2.5,
            border: `1px solid ${color}22`, overflow: 'hidden',
          }}>
            {filtered.map((event, i) => (
              <EventRow
                key={event.id ?? i}
                event={event}
                color={color}
                onClick={onEventClick}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
});

// ── PDF Export ────────────────────────────────────────────────────────────────

const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

const exportPDF = (resultData, appliedFilters, areaBreakdown, totalShown) => {
  const doc   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const teal  = [26, 188, 156];
  const dark  = [22, 28, 36];
  const grey  = [100, 116, 139];

  doc.setFillColor(...dark);
  doc.rect(0, 0, pageW, 28, 'F');
  doc.setFillColor(...teal);
  doc.rect(0, 28, pageW, 2, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('VTS Alert Report', 14, 12);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(160, 180, 200);
  doc.text('Vehicle Tracking System — CCL Alert Dashboard', 14, 19);

  if (appliedFilters) {
    const range = `${toShortDate(appliedFilters.fromDate)}  →  ${toShortDate(appliedFilters.toDate)}`;
    doc.setTextColor(26, 188, 156);
    doc.text(range, pageW - 14, 12, { align: 'right' });
    const areas = appliedFilters.selectedAreas.join(', ');
    doc.setTextColor(160, 180, 200);
    doc.text(`Areas: ${areas}`, pageW - 14, 19, { align: 'right' });
  }

  let y = 38;

  doc.setTextColor(...grey);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('SUMMARY', 14, y);
  y += 4;

  const kpiW = (pageW - 28 - 12) / 5;
  EVENT_TYPES.forEach((t, i) => {
    const x   = 14 + i * (kpiW + 3);
    const cnt = (resultData[t.id] || []).length;
    const pct = totalShown > 0 ? Math.round((cnt / totalShown) * 100) : 0;
    const col = hexToRgb(t.color);

    doc.setFillColor(col[0], col[1], col[2], 0.08);
    doc.setDrawColor(col[0], col[1], col[2]);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, kpiW, 18, 2, 2, 'FD');

    doc.setFillColor(col[0], col[1], col[2]);
    doc.rect(x, y, kpiW, 1.5, 'F');

    doc.setTextColor(...col);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(String(cnt), x + kpiW / 2, y + 9, { align: 'center' });

    doc.setFontSize(6.5);
    doc.setTextColor(80, 100, 120);
    doc.setFont('helvetica', 'normal');
    doc.text(t.label, x + kpiW / 2, y + 13.5, { align: 'center' });

    doc.setTextColor(...col);
    doc.setFontSize(6);
    doc.text(`${pct}%`, x + kpiW / 2, y + 17, { align: 'center' });
  });
  y += 24;

  if (areaBreakdown.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...grey);
    doc.text('AREA BREAKDOWN', 14, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head:   [['Area', 'Events', '% of Total']],
      body:   areaBreakdown.map(([area, count]) => [area, count, `${totalShown > 0 ? Math.round((count / totalShown) * 100) : 0}%`]),
      theme:  'grid',
      styles: { fontSize: 8, cellPadding: 2.5, textColor: [50, 60, 75] },
      headStyles:           { fillColor: teal, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.5 },
      alternateRowStyles:   { fillColor: [245, 248, 250] },
      columnStyles:         { 1: { halign: 'center' }, 2: { halign: 'center' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  EVENT_TYPES.forEach((t) => {
    const events = resultData[t.id] || [];
    if (events.length === 0) return;

    if (y > 230) { doc.addPage(); y = 16; }

    const col = hexToRgb(t.color);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...col);
    doc.text(`${t.label.toUpperCase()}  (${events.length} events)`, 14, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head:   [['Vehicle No.', 'Date & Time', 'Area', 'Geofence / Route', 'Transporter', 'Severity']],
      body:   events.map((e) => [
        e.vehicleNumber || '—',
        e.readableTime  || '—',
        e.areaDisplay   || '—',
        e.geofenceName || e.groupName || '—',
        e.vehicleTransporter || '—',
        e.severity || 'High',
      ]),
      theme:  'striped',
      styles: { fontSize: 7, cellPadding: 2, textColor: [40, 55, 70], overflow: 'ellipsize' },
      headStyles:         { fillColor: col, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 0: { cellWidth: 24 }, 1: { cellWidth: 32 }, 2: { cellWidth: 24 }, 3: { cellWidth: 32 }, 4: { cellWidth: 30 }, 5: { cellWidth: 16, halign: 'center' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  });

  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFillColor(245, 247, 250);
    doc.rect(0, doc.internal.pageSize.getHeight() - 10, pageW, 10, 'F');
    doc.setFontSize(7);
    doc.setTextColor(...grey);
    doc.text(`Generated: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}  |  CCL Alert Dashboard — VTS Report`, 14, doc.internal.pageSize.getHeight() - 4);
    doc.text(`Page ${p} of ${totalPages}`, pageW - 14, doc.internal.pageSize.getHeight() - 4, { align: 'right' });
  }

  doc.save(`VTS_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
};

// ── Main VTSReportsView ───────────────────────────────────────────────────────

const VTSReportsView = () => {
  const userIsAdmin = isAdminUser();
  const userArea    = getLoggedInArea();

  const [viewMode,       setViewMode]       = useState('filter');
  const [fromDate,       setFromDate]       = useState(new Date(new Date().setHours(0, 0, 0, 0)));
  const [toDate,         setToDate]         = useState(new Date(new Date().setHours(23, 59, 59, 999)));
  const [selectedTypes,  setSelectedTypes]  = useState(['OR', 'OA', 'TA', 'OS', 'USP']);
  const [selectedAreas,  setSelectedAreas]  = useState(
    !userIsAdmin && userArea ? [userArea] : ['All']
  );
  const [isLoading,      setIsLoading]      = useState(false);
  const [isExporting,    setIsExporting]    = useState(false);
  const [error,          setError]          = useState(null);
  const [resultData,     setResultData]     = useState({});
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [mapOpen,        setMapOpen]        = useState(false);
  const [activeEvent,    setActiveEvent]    = useState(null);
  const [fullscreenType, setFullscreenType] = useState(null);

  const handleTypeToggle = useCallback((id) =>
    setSelectedTypes((prev) => prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]),
  []);

  const handleAreaChange = useCallback((e) => {
    if (!userIsAdmin && userArea) return;
    const val  = e.target.value;
    if (!val || val.length === 0) { setSelectedAreas(['All']); return; }
    const last = val[val.length - 1];
    if (last === 'All') { setSelectedAreas(['All']); return; }
    const without = val.filter((v) => v !== 'All');
    setSelectedAreas(without.length === 0 ? ['All'] : without);
  }, [userIsAdmin, userArea]);

  const handleApply = async () => {
    if (selectedTypes.length === 0) { setError('Please select at least one alert type.'); return; }
    setIsLoading(true); setError(null);
    try {
      const url  = `${VTS_API_BASE}/api/reports/filter?from=${encodeURIComponent(formatISO(fromDate))}&to=${encodeURIComponent(formatISO(toDate))}&types=${selectedTypes.join(',')}`;
      const res  = await fetch(url);
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const json = await res.json();
      const raw  = json.data || {};

      const adminAreaKeys = userIsAdmin
        ? (selectedAreas.includes('All') ? null : selectedAreas.map((a) => DISPLAY_TO_VTS_AREA[a]).filter(Boolean))
        : null;

      const transformed = {};
      EVENT_TYPES.forEach((type) => {
        let items = (raw[type.apiKey] || []).map((item) => transformEvent(item, type.alertType));
        if (!userIsAdmin && userArea) {
          // Resolve auth area key → VTS area field value.
          // AUTH_TO_VTS_AREA handles compound keys (e.g. 'magadh-sanghmitra' → 'magadh')
          // whose normalised form doesn't match the DB area field.
          // Fall back to normalizeAreaKey comparison for any unmapped key.
          const vtsKey = AUTH_TO_VTS_AREA[userArea];
          if (vtsKey) {
            items = items.filter((item) => item.area === vtsKey);
          } else {
            const ua = normalizeAreaKey(userArea);
            items = items.filter((item) => normalizeAreaKey(item.area) === ua);
          }
        } else if (adminAreaKeys && adminAreaKeys.length > 0) {
          items = items.filter((item) => adminAreaKeys.includes(item.area));
        }
        transformed[type.id] = items;
      });

      setResultData(transformed);
      setAppliedFilters({ fromDate, toDate, selectedTypes, selectedAreas });
      setViewMode('results');
    } catch (err) {
      setError(`Failed to fetch VTS data: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEventClick = useCallback((event) => { setActiveEvent(event); setMapOpen(true); }, []);

  const totalShown = useMemo(
    () => Object.values(resultData).reduce((s, arr) => s + arr.length, 0),
    [resultData]
  );

  const areaBreakdown = useMemo(() => {
    const counts = {};
    Object.values(resultData).forEach((events) => {
      events.forEach((ev) => {
        const area = ev.areaGroup || ev.areaDisplay || 'Unknown';
        counts[area] = (counts[area] || 0) + 1;
      });
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [resultData]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try { exportPDF(resultData, appliedFilters, areaBreakdown, totalShown); }
    catch (e) { console.error('PDF export error:', e); }
    finally { setIsExporting(false); }
  }, [resultData, appliedFilters, areaBreakdown, totalShown]);

  // ── Always-run derived state & effects (must be before any early return) ────

  const activeTypes = EVENT_TYPES.filter((t) =>
    (appliedFilters?.selectedTypes || []).includes(t.id)
  );

  React.useEffect(() => {
    if (!fullscreenType) return;
    const handler = (e) => { if (e.key === 'Escape') setFullscreenType(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [fullscreenType]);

  // ── FILTER VIEW ─────────────────────────────────────────────────────────────

  if (viewMode === 'filter') {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 130px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3, md: 4 },
          background: 'radial-gradient(ellipse at 50% 50%, rgba(59,130,246,0.06) 0%, transparent 65%)',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 780 }}>

          {/* ── Page header ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box sx={{
              width: 42, height: 42, borderRadius: '11px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, rgba(59,130,246,0.35), rgba(59,130,246,0.1))',
              border: '1.5px solid rgba(59,130,246,0.4)',
              color: '#3B82F6', flexShrink: 0,
            }}>
              <LocalShippingIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem', letterSpacing: 0.3 }}>
                  Vehicle Tracking Report Filters
                </Typography>
                <Box component="span" sx={{
                  fontSize: '0.68rem', fontWeight: 700, px: 0.8, py: 0.15,
                  borderRadius: '5px', background: 'rgba(59,130,246,0.18)',
                  color: '#3B82F6', border: '1px solid rgba(59,130,246,0.35)',
                  letterSpacing: 0.5,
                }}>
                  VTS
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                Configure filters to generate a vehicle tracking alert report
              </Typography>
            </Box>
          </Box>

          {/* ── Main filter card ── */}
          <Box sx={{
            background: 'rgba(22,30,42,0.92)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '14px',
            p: { xs: 2.5, sm: 3 },
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.45)',
          }}>

            {/* Time Range */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 0.8 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(26,188,156,0.15)', color: '#1ABC9C', flexShrink: 0 }}>
                <ScheduleIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.9)', fontSize: '0.82rem', letterSpacing: 0.4 }}>Time Range</Typography>
              <Tooltip title="Select the reporting period" placement="top">
                <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.3)', p: 0.3, ml: 0.2 }}>
                  <HelpOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <DateTimePicker label="From" value={fromDate} onChange={setFromDate} ampm={false}
                  slotProps={{ textField: { fullWidth: true, size: 'small', sx: S.dateTimeField } }} />
                <DateTimePicker label="To" value={toDate} onChange={setToDate} ampm={false}
                  slotProps={{ textField: { fullWidth: true, size: 'small', sx: S.dateTimeField } }} />
              </Box>
            </LocalizationProvider>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2.5 }} />

            {/* Alert Types */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 0.8 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(26,188,156,0.15)', color: '#1ABC9C', flexShrink: 0 }}>
                <CategoryIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.9)', fontSize: '0.82rem', letterSpacing: 0.4 }}>Alert Types</Typography>
              <Tooltip title="Select which alert categories to include" placement="top">
                <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.3)', p: 0.3, ml: 0.2 }}>
                  <HelpOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {EVENT_TYPES.map(({ id, label, color, Icon }) => {
                const active = selectedTypes.includes(id);
                return (
                  <Box key={id} onClick={() => handleTypeToggle(id)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      px: 1.5, py: 0.85,
                      border: `1.5px solid ${active ? color : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '8px',
                      bgcolor: active ? `${color}14` : 'rgba(255,255,255,0.03)',
                      cursor: 'pointer', transition: 'all 0.15s ease',
                      boxShadow: active ? `0 0 12px ${color}20` : 'none',
                      '&:hover': { bgcolor: active ? `${color}1f` : 'rgba(255,255,255,0.06)', borderColor: color },
                    }}
                  >
                    <Icon sx={{ color: active ? color : 'rgba(255,255,255,0.3)', fontSize: 15, transition: 'color 0.15s' }} />
                    <Typography variant="caption" sx={{ color: active ? 'white' : 'rgba(255,255,255,0.5)', fontWeight: active ? 700 : 400, fontSize: '0.8rem' }}>
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2.5 }} />

            {/* Area */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 0.8 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(26,188,156,0.15)', color: '#1ABC9C', flexShrink: 0 }}>
                <LocationOnIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.9)', fontSize: '0.82rem', letterSpacing: 0.4 }}>Area</Typography>
              <Tooltip title="Filter by one or more deployment areas" placement="top">
                <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.3)', p: 0.3, ml: 0.2 }}>
                  <HelpOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {!userIsAdmin ? (
              <Box sx={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
                px: 1.5, py: 1,
                background: 'rgba(26,188,156,0.05)',
                border: '1px solid rgba(26,188,156,0.25)',
                borderRadius: '8px',
              }}>
                <LocationOnIcon sx={{ fontSize: 16, color: '#1ABC9C', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem' }}>Area:</Typography>
                <Chip label={getAreaDisplayName(userArea) || userArea || 'N/A'} size="small"
                  sx={{ background: 'rgba(26,188,156,0.2)', color: '#1ABC9C', fontWeight: 700, fontSize: '0.75rem', border: '1px solid rgba(26,188,156,0.4)' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem', fontStyle: 'italic' }}>(restricted)</Typography>
              </Box>
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.84rem' }}>Select areas</InputLabel>
                <Select multiple value={selectedAreas} label="Select areas" onChange={handleAreaChange}
                  sx={S.selectInput} MenuProps={S.selectMenu}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small"
                          sx={{ background: 'rgba(26,188,156,0.18)', color: 'white', fontSize: '0.72rem', height: 20 }} />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="All" sx={S.menuItem}>All Areas</MenuItem>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 0.5 }} />
                  {AREA_OPTIONS.filter((a) => a.value !== 'All').map((area) => (
                    <MenuItem key={area.value} value={area.value} sx={S.menuItem}>{area.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(220,20,60,0.1)', color: '#ff6b6b', border: '1px solid rgba(220,20,60,0.25)', borderRadius: '8px', '& .MuiAlert-icon': { color: '#ff6b6b' } }}>
                {error}
              </Alert>
            )}

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 2.5 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleApply}
                disabled={isLoading || selectedTypes.length === 0}
                startIcon={isLoading ? <CircularProgress size={15} sx={{ color: 'inherit' }} /> : <SearchIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #1ABC9C, #16a085)',
                  color: 'white', fontWeight: 700, fontSize: '0.85rem',
                  letterSpacing: 0.8, px: 4, py: 1.1, borderRadius: '8px',
                  boxShadow: '0 4px 16px rgba(26,188,156,0.25)',
                  '&:hover': { background: 'linear-gradient(135deg, #1ed2b0, #1ABC9C)', boxShadow: '0 6px 20px rgba(26,188,156,0.35)' },
                  '&:disabled': { background: 'rgba(26,188,156,0.15)', color: 'rgba(255,255,255,0.25)' },
                }}>
                {isLoading ? 'Generating…' : 'Generate Report'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── RESULTS VIEW ─────────────────────────────────────────────────────────────

  return (
    <Box sx={{
      minHeight: 'calc(100vh - 130px)',
      display: 'flex', flexDirection: 'column',
      p: { xs: 1.5, sm: 2, md: 2.5 }, gap: 2,
    }}>

      {/* ── Results Header ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => setViewMode('filter')} size="small"
            sx={{
              color: 'rgba(255,255,255,0.6)', bgcolor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: 'white' },
            }}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>
              VTS Alert Report
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.71rem' }}>
              {appliedFilters && `${toShortDate(appliedFilters.fromDate)} → ${toShortDate(appliedFilters.toDate)}`}
            </Typography>
          </Box>
          <Chip label={`${totalShown} events`} size="small"
            sx={{ bgcolor: 'rgba(26,188,156,0.15)', color: '#1ABC9C', border: '1px solid rgba(26,188,156,0.3)', fontWeight: 700, fontSize: '0.72rem' }} />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<FilterAltIcon sx={{ fontSize: 15 }} />}
            onClick={() => setViewMode('filter')}
            sx={{
              color: 'rgba(255,255,255,0.65)', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '8px', fontSize: '0.78rem', fontWeight: 600, px: 1.75, py: 0.85,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.25)' },
            }}>
            Modify
          </Button>
          <Button size="small" variant="contained"
            startIcon={isExporting
              ? <CircularProgress size={13} sx={{ color: 'inherit' }} />
              : <PictureAsPdfIcon sx={{ fontSize: 15 }} />}
            onClick={handleExport}
            disabled={isExporting || totalShown === 0}
            sx={{
              background: 'linear-gradient(135deg, #c0392b, #e74c3c)', color: 'white',
              fontWeight: 700, fontSize: '0.78rem', borderRadius: '8px', px: 2, py: 0.85,
              boxShadow: '0 3px 12px rgba(231,76,60,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #e74c3c, #c0392b)' },
              '&:disabled': { background: 'rgba(231,76,60,0.2)', color: 'rgba(255,255,255,0.3)' },
            }}>
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* ── KPI Cards ── */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {activeTypes.map((t) => (
          <Box key={t.id} sx={{ flex: '1 1 0', minWidth: 0 }}>
            <KpiCard typeConfig={t} count={(resultData[t.id] || []).length} total={totalShown} />
          </Box>
        ))}
      </Box>

      {/* ── Area Breakdown ── */}
      <AreaBreakdownRow areaBreakdown={areaBreakdown} total={totalShown} />

      {/* ── Event Cards Grid ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
        gap: 2,
        alignItems: 'start',
        pb: 3,
      }}>
        {activeTypes.map((t) => (
          <EventTypeCard
            key={t.id}
            typeConfig={t}
            events={resultData[t.id] || []}
            onEventClick={handleEventClick}
            onExpand={() => setFullscreenType(t.id)}
          />
        ))}
      </Box>

      {/* ── Fullscreen modal ── */}
      {fullscreenType && (() => {
        const tc  = EVENT_TYPES.find((t) => t.id === fullscreenType);
        const evs = resultData[fullscreenType] || [];
        return (
          <FullscreenTypeModal
            typeConfig={tc}
            events={evs}
            onClose={() => setFullscreenType(null)}
            onEventClick={handleEventClick}
          />
        );
      })()}

      {/* ── Map modal ── */}
      {/* Keep activeEvent alive while modal is open so fullscreen modal stays
          intact behind it. Clear activeEvent only after dialog finishes closing. */}
      {activeEvent && (
        <VehicleAlertMapModal
          event={activeEvent}
          open={mapOpen}
          onClose={() => setMapOpen(false)}
          TransitionProps={{ onExited: () => setActiveEvent(null) }}
        />
      )}
    </Box>
  );
};

export default VTSReportsView;
